import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { saveGoal, saveMilestones } from '../service/GoalMilestoneService';
import '../css/GoalMilestoneCard.css';

const MILESTONE_EXAMPLES = [
    {
        name: 'Urge without acting',
        desc: 'I noticed the urge to scroll and chose not to act on it',
    },
    {
        name: 'Present in conversation',
        desc: 'I was in a conversation without planning my response or checking my phone',
    },
    {
        name: 'Earned rest',
        desc: 'I rested and it felt earned, not like wasted time',
    },
];

function newMilestoneId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `ms-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function progressSubLabel(done, total) {
    if (total === 0) return '';
    if (done === 0) return 'Each milestone marks real progress toward your goal';
    if (done < total) return "You're building momentum — keep going";
    return 'Every milestone reached — your goal is alive in daily life';
}

function PencilIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
                d="M5 12l4 4 10-10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function ChevronIcon({ open }) {
    return (
        <svg
            className={`goalMilestoneChevron${open ? ' is-open' : ''}`}
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
        >
            <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

const GoalMilestoneCard = ({
    userId,
    initialGoal = '',
    initialMilestones = [],
    onGoalUpdate,
    onMilestonesUpdate,
}) => {
    const [goal, setGoal] = useState(initialGoal || '');
    const [milestones, setMilestones] = useState(
        Array.isArray(initialMilestones) ? initialMilestones : []
    );
    const [editingGoal, setEditingGoal] = useState(false);
    const [goalDraft, setGoalDraft] = useState(initialGoal || '');
    const [milestonesOpen, setMilestonesOpen] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalName, setModalName] = useState('');
    const [modalDesc, setModalDesc] = useState('');

    useEffect(() => {
        setGoal(initialGoal || '');
        setGoalDraft(initialGoal || '');
    }, [initialGoal]);

    useEffect(() => {
        setMilestones(Array.isArray(initialMilestones) ? initialMilestones : []);
    }, [initialMilestones]);

    const doneCount = useMemo(
        () => milestones.filter((m) => m.done).length,
        [milestones]
    );
    const totalCount = milestones.length;
    const progressPct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
    const hasMilestones = totalCount > 0;

    const persistGoal = useCallback(
        async (nextGoal) => {
            try {
                await saveGoal(userId, nextGoal);
            } catch (err) {
                console.error('GoalMilestoneCard: saveGoal failed', err);
            }
        },
        [userId]
    );

    const persistMilestones = useCallback(
        async (nextMilestones) => {
            try {
                await saveMilestones(userId, nextMilestones);
            } catch (err) {
                console.error('GoalMilestoneCard: saveMilestones failed', err);
            }
        },
        [userId]
    );

    const updateMilestones = useCallback(
        (updater) => {
            setMilestones((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                onMilestonesUpdate?.(next);
                persistMilestones(next);
                return next;
            });
        },
        [onMilestonesUpdate, persistMilestones]
    );

    const handleSaveGoal = () => {
        const trimmed = goalDraft.trim();
        setGoal(trimmed);
        setEditingGoal(false);
        onGoalUpdate?.(trimmed);
        persistGoal(trimmed);
    };

    const handleCancelGoal = () => {
        setGoalDraft(goal);
        setEditingGoal(false);
    };

    const handleToggleDone = (id) => {
        updateMilestones((prev) =>
            prev.map((m) => (m.id === id ? { ...m, done: !m.done } : m))
        );
    };

    const handleDelete = (id) => {
        updateMilestones((prev) => prev.filter((m) => m.id !== id));
    };

    const openModal = () => {
        setModalName('');
        setModalDesc('');
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    const handleAddMilestone = () => {
        const name = modalName.trim();
        const desc = modalDesc.trim();
        if (!name && !desc) return;

        const entry = {
            id: newMilestoneId(),
            name: name || desc.slice(0, 48),
            desc: desc || name,
            done: false,
            createdAt: new Date().toISOString(),
        };

        updateMilestones((prev) => [...prev, entry]);
        setMilestonesOpen(true);
        closeModal();
    };

    const applyExample = (example) => {
        setModalName(example.name);
        setModalDesc(example.desc);
    };

    const displayGoal = goal.trim();

    return (
        <>
            <article className="goalMilestoneCard" aria-label="Goal and milestones">
                {/* 1. Goal section */}
                <section className="goalMilestoneSection">
                    <div className="goalMilestoneSectionHead">
                        <p className="goalMilestoneEyebrow">
                            <span className="highlight">Your Goal:</span>
                        </p>
                        {!editingGoal && (
                            <button
                                type="button"
                                className="goalMilestoneEditBtn"
                                onClick={() => {
                                    setGoalDraft(goal);
                                    setEditingGoal(true);
                                }}
                                aria-label="Edit goal"
                            >
                                <PencilIcon />
                                Edit goal
                            </button>
                        )}
                    </div>

                    {editingGoal ? (
                        <>
                            <textarea
                                className="goalMilestoneEditArea"
                                value={goalDraft}
                                onChange={(e) => setGoalDraft(e.target.value)}
                                aria-label="Edit your goal"
                                rows={4}
                            />
                            <div className="goalMilestoneEditActions">
                                <button
                                    type="button"
                                    className="goalMilestoneBtnPrimary"
                                    onClick={handleSaveGoal}
                                >
                                    Save
                                </button>
                                <button
                                    type="button"
                                    className="goalMilestoneBtnGhost"
                                    onClick={handleCancelGoal}
                                >
                                    Cancel
                                </button>
                            </div>
                        </>
                    ) : (
                        <blockquote className={`goalMilestoneQuote${displayGoal ? '' : ' goalMilestoneQuote--empty'}`}>
                            {displayGoal ? `"${displayGoal}"` : 'Set a goal that matters to you'}
                        </blockquote>
                    )}
                </section>

                {hasMilestones ? (
                    <>
                        {/* 2. Progress bar */}
                        <section className="goalMilestoneSection goalMilestoneProgress">
                            <div className="goalMilestoneProgressMeta">
                                <p className="goalMilestoneProgressCount">
                                    {doneCount} of {totalCount} reached
                                </p>
                                <p className="goalMilestoneProgressHint">{progressPct}%</p>
                            </div>
                            <div
                                className="goalMilestoneProgressTrack"
                                role="progressbar"
                                aria-valuenow={doneCount}
                                aria-valuemin={0}
                                aria-valuemax={totalCount}
                                aria-label={`${doneCount} of ${totalCount} milestones reached`}
                            >
                                <div
                                    className="goalMilestoneProgressFill"
                                    style={{ width: `${progressPct}%` }}
                                />
                            </div>
                            <p className="goalMilestoneProgressSub">
                                {progressSubLabel(doneCount, totalCount)}
                            </p>
                        </section>

                        {/* 3. Toggle row */}
                        <section className="goalMilestoneSection">
                            <button
                                type="button"
                                className="goalMilestoneToggleRow"
                                onClick={() => setMilestonesOpen((o) => !o)}
                                aria-expanded={milestonesOpen}
                            >
                                <span className="goalMilestoneToggleLeft">
                                    <span>Milestones</span>
                                    <span className="goalMilestoneBadge">
                                        {doneCount}/{totalCount}
                                    </span>
                                </span>
                                <ChevronIcon open={milestonesOpen} />
                            </button>
                        </section>

                        {/* 4. Milestone list */}
                        <div
                            className={`goalMilestoneListWrap${milestonesOpen ? ' is-open' : ''}`}
                        >
                            <div className="goalMilestoneList">
                                {milestones.map((m) => (
                                    <div
                                        key={m.id}
                                        className={`goalMilestoneRow${m.done ? ' is-done' : ''}`}
                                    >
                                        <button
                                            type="button"
                                            className={`goalMilestoneCheck${m.done ? ' is-checked' : ''}`}
                                            onClick={() => handleToggleDone(m.id)}
                                            aria-label={
                                                m.done
                                                    ? `Mark "${m.name}" incomplete`
                                                    : `Mark "${m.name}" complete`
                                            }
                                            aria-pressed={m.done}
                                        >
                                            <CheckIcon />
                                        </button>
                                        <div className="goalMilestoneRowBody">
                                            <p className="goalMilestoneRowName">{m.name}</p>
                                            {m.desc ? (
                                                <p className="goalMilestoneRowDesc">{m.desc}</p>
                                            ) : null}
                                        </div>
                                        <button
                                            type="button"
                                            className="goalMilestoneDelete"
                                            onClick={() => handleDelete(m.id)}
                                            aria-label={`Delete milestone ${m.name}`}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="goalMilestoneAddRow"
                                    onClick={openModal}
                                >
                                    + Add a milestone
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <section className="goalMilestoneEmpty">
                        <p className="goalMilestoneEmptyPrompt">
                            What would tell you the work is working?
                        </p>
                        <p className="goalMilestoneEmptyCopy">
                            Milestones are small, observable signs that you're making progress towards your goal.
                        </p>
                        <button
                            type="button"
                            className="goalMilestoneEmptyCta"
                            onClick={openModal}
                        >
                            Add your first milestone
                        </button>
                    </section>
                )}
            </article>

            {modalOpen && (
                <div
                    className="goalMilestoneModalBackdrop"
                    role="presentation"
                    onClick={closeModal}
                >
                    <div
                        className="goalMilestoneSheet"
                        role="dialog"
                        aria-labelledby="goal-milestone-modal-title"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 id="goal-milestone-modal-title" className="goalMilestoneSheetTitle">
                            Add milestone
                        </h3>

                        <label className="goalMilestoneSheetLabel" htmlFor="milestone-name">
                            Name
                        </label>
                        <input
                            id="milestone-name"
                            className="goalMilestoneSheetInput"
                            type="text"
                            value={modalName}
                            onChange={(e) => setModalName(e.target.value)}
                            placeholder="Short title"
                        />

                        <label className="goalMilestoneSheetLabel" htmlFor="milestone-desc">
                            Description
                        </label>
                        <textarea
                            id="milestone-desc"
                            className="goalMilestoneSheetDesc"
                            value={modalDesc}
                            onChange={(e) => setModalDesc(e.target.value)}
                            placeholder="What would you notice in daily life?"
                            rows={3}
                        />

                        <div className="goalMilestoneExamples">
                            {MILESTONE_EXAMPLES.map((ex) => (
                                <button
                                    key={ex.desc}
                                    type="button"
                                    className="goalMilestoneExampleChip"
                                    onClick={() => applyExample(ex)}
                                >
                                    {ex.desc}
                                </button>
                            ))}
                        </div>

                        <button
                            type="button"
                            className="goalMilestoneBtnPrimary goalMilestoneSheetSave"
                            onClick={handleAddMilestone}
                        >
                            Add milestone
                        </button>
                        <button
                            type="button"
                            className="goalMilestoneSheetCancel"
                            onClick={closeModal}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default GoalMilestoneCard;
